import React, { FC, useMemo } from 'react';
import { APIReferenceMember, ReferenceType } from '@modules/client';
import { Grid, useMediaQuery } from '@rbx/ui';
import StandardMarkdown from '@modules/ui/markdown/components/StandardMarkdown';
import { getVisibleReferenceTags } from '../utils/tagUtils';
import useReferenceSummaryMemberTableStyles from './ReferenceSummaryMemberTable.styles';
import ReferenceSummaryMemberTrTitle from './ReferenceSummaryMemberTrTitle';
import ReferenceMemberTags from './ReferenceMemberTags';

interface ReferenceSummaryMemberTrProps {
  ariaLabel: string;
  referenceName: string;
  member: APIReferenceMember;
  referenceType: ReferenceType;
  isEventMemberType: boolean;
  isSameNameAsPrevMember: boolean;
  isInheritedMember?: boolean;
}

const ReferenceSummaryMemberTr: FC<React.PropsWithChildren<ReferenceSummaryMemberTrProps>> = ({
  member,
  referenceName,
  referenceType,
  ariaLabel,
  isEventMemberType,
  isSameNameAsPrevMember,
  isInheritedMember,
}) => {
  const {
    classes: { tr, trTags, summary },
    cx,
  } = useReferenceSummaryMemberTableStyles();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('Medium'));

  const visibleTags = useMemo(() => {
    return getVisibleReferenceTags(member);
  }, [member]);

  return (
    <li key={ariaLabel} className={cx(tr, 'summary-tr')} aria-label={ariaLabel} role='row'>
      <Grid container>
        <Grid item XSmall paddingRight={2} marginBottom={0}>
          <ReferenceSummaryMemberTrTitle
            isSameNameAsPrevMember={isSameNameAsPrevMember}
            referenceName={referenceName}
            member={member}
            referenceType={referenceType}
            isEventMemberType={isEventMemberType}
            isInheritedMember={isInheritedMember}
          />
        </Grid>
        {!isMobile && visibleTags?.length > 0 ? (
          <Grid item XSmall Medium='auto' className={trTags}>
            <ReferenceMemberTags tags={visibleTags} member={member} />
          </Grid>
        ) : null}
      </Grid>
      {isMobile && visibleTags?.length > 0 && (
        <Grid item textAlign='left' className={trTags} XSmall>
          <ReferenceMemberTags tags={visibleTags} member={member} />
        </Grid>
      )}
      {member.summary && (
        <StandardMarkdown
          paragraphProps={{
            variant: 'body1',
            className: summary,
            sx: {
              margin: 0,
            },
          }}>
          {member.summary}
        </StandardMarkdown>
      )}
    </li>
  );
};

export default ReferenceSummaryMemberTr;
